// ts-rest contract for the AML Assessment (SEP-171).
//
// An `AML-####` record per assessment occasion, stacked on a Contact over time —
// the structural sibling of the Vulnerability Assessment contract. The Contact
// detail page needs two things:
//   - the summary the AML card shows ("AML Risk: Low / Medium / High", from the
//     most recently COMPLETED assessment),
//   - the stacked history rows (Score | Contact | Completed By | Completed
//     Date).
//
// Only the selected option values + the conditional high-risk occupation are
// stored; the per-question score, the total and the band are computed by the
// shared `computeAmlAssessment` helper (@septima/contract), which the web form
// recomputes live. The question table itself is imported directly from the
// package (`AML_QUESTIONS`, `AML_HIGH_RISK_OCCUPATIONS`); it is not an endpoint.

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

import { staffActionErrorSchema } from './agent';

const c = initContract();

import { errorBody } from '../schemas/common';

/**
 * `superseded` (SEP-179) — a record a later correction restates. It stays in
 * the history grid as evidence; every "current assessment" read filters on
 * `completed`.
 */
// SEP-228 adds the two states between Ops and Compliance: `awaiting_compliance`
// holds a Moderate or High record until Compliance decides, and `rejected` is the
// terminal state of a client Compliance will not onboard.
const statusSchema = z.enum([
    'draft',
    'awaiting_compliance',
    'completed',
    'rejected',
    'superseded',
]);
const riskBandSchema = z.enum(['low', 'medium', 'high']);

/**
 * The stored answers: question key → the selected option `value` (`'yes'` /
 * `'no'`). Only answered questions are present.
 */
export const amlAnswersSchema = z.record(z.string(), z.string());
export type AmlAnswersDto = z.infer<typeof amlAnswersSchema>;

/** Controller-derived figures (mirror of `computeAmlAssessment`). */
export const amlAssessmentDerivedSchema = z.object({
    /** Sum of the selected options' scores. */
    score: z.number(),
    /** Count of questions with a recognised answer. */
    answered: z.number(),
    /** Number of questions on the form. */
    answerable: z.number(),
    /** Per-question contribution, keyed by question key — the form's score boxes. */
    questionScores: z.record(z.string(), z.number()),
    band: riskBandSchema,
    /** "Low" / "Medium" / "High". */
    bandLabel: z.string(),
    /**
     * Whether the stored answers key into the SEP-171 question set. A SEP-228
     * answer set reads `legacy` here, and every surface that renders a band gates
     * on it, because an unrecognised answer set scores 0 and bands Low.
     */
    answersInstrument: z.enum(['empty', 'current', 'legacy']),
});

/**
 * The SEP-228 derived block (mirror of `computeAmlQuestionnaire`). Present on a
 * `kind`-bearing record and null on a SEP-171 one.
 *
 * `result` is null while the record is a draft: §9 says Ops answers with no
 * running total in front of them, so the score and the band do not travel until
 * the record is submitted.
 */
export const amlQuestionnaireResultSchema = z.object({
    score: z.number(),
    band: z.enum(['low', 'moderate', 'high']),
    /** "Low" / "Moderate" / "High". */
    bandLabel: z.string(),
});
export type AmlQuestionnaireResultDto = z.infer<typeof amlQuestionnaireResultSchema>;

export const amlQuestionnaireDerivedSchema = z.object({
    kind: z.enum(['individual', 'company']),
    /** The score and the band. Null while the record is a draft (§9, §21). */
    result: amlQuestionnaireResultSchema.nullable(),
    /** The visible question keys, in render order, after the conditional rules. */
    visibleKeys: z.array(z.string()),
    /** The per-question contribution. Null while the record is a draft. */
    questionScores: z.record(z.string(), z.number()).nullable(),
    /** Why a country or date question scored what it did (§39). */
    questionReasons: z.record(z.string(), z.string()),
    /** The mandatory questions still unanswered. The submit door reads it (§40). */
    missingKeys: z.array(z.string()),
    /** Enhanced due diligence is mandatory (§25). */
    eddRequired: z.boolean(),
    /** Each trigger that makes EDD mandatory, in the words Compliance reads. */
    eddReasons: z.array(z.string()),
});
export type AmlQuestionnaireDerivedDto = z.infer<typeof amlQuestionnaireDerivedSchema>;

export const amlEddStatusSchema = z.enum([
    'not_required',
    'not_started',
    'in_progress',
    'completed',
]);
export type AmlEddStatusDto = z.infer<typeof amlEddStatusSchema>;

export const amlDecisionSchema = z.enum(['pending', 'approved', 'rejected']);
export type AmlDecisionDto = z.infer<typeof amlDecisionSchema>;

export const amlAssessmentDtoSchema = z.object({
    id: z.string(),
    /** AML-####. */
    reference: z.string().nullable(),
    contactId: z.string(),
    /** The Contact's display name — the history grid's "Contact" column. */
    contactName: z.string().nullable(),
    /** The owning IM (a `users` row id). */
    imUserId: z.string(),
    status: statusSchema,
    answers: amlAnswersSchema,
    /** The conditional cash-generative-business selection; null unless the
     * occupation question is answered `yes`. */
    highRiskOccupation: z.string().nullable(),
    derived: amlAssessmentDerivedSchema,
    /** Calendar completion date (`YYYY-MM-DD`) — null while draft. */
    completionDate: z.string().nullable(),
    createdAt: z.string(),
    /** Completion timestamp — null while draft. */
    completedAt: z.string().nullable(),
    /** The "Completed By" staff user id — null while draft. */
    completedByUserId: z.string().nullable(),
    /** Display name for `completedByUserId`, resolved server-side. */
    completedByName: z.string().nullable(),
    // --- Amendment chain (SEP-179) ---
    /** On a CORRECTION: the id of the assessment it restates. */
    supersedesId: z.string().nullable(),
    /** On a CORRECTION: that assessment's AML-####, resolved server-side. */
    supersedesReference: z.string().nullable(),
    /** On a SUPERSEDED record: the id of the correction that replaced it. */
    supersededById: z.string().nullable(),
    /** On a SUPERSEDED record: that correction's AML-####. */
    supersededByReference: z.string().nullable(),
    /** When the correction was recorded. */
    supersededAt: z.string().nullable(),
    /** Who recorded the correction. */
    supersededByUserId: z.string().nullable(),
    /** Display name for `supersededByUserId`, resolved server-side. */
    supersededByName: z.string().nullable(),
    /** On a CORRECTION: the free-text reason given. */
    amendmentReason: z.string().nullable(),
    // --- The SEP-228 workflow. Every field is null on a SEP-171 record. -----
    /** Which question set the record answers. Null marks a SEP-171 record. */
    kind: z.enum(['individual', 'company']).nullable(),
    /** The SEP-228 derived block. Null on a SEP-171 record. */
    questionnaire: amlQuestionnaireDerivedSchema.nullable(),
    submittedAt: z.string().nullable(),
    submittedByUserId: z.string().nullable(),
    submittedByName: z.string().nullable(),
    /** The figure the instrument gave at submission, kept through an override. */
    calculatedScore: z.number().nullable(),
    calculatedBand: z.enum(['low', 'moderate', 'high']).nullable(),
    /** The risk that stands: the override where there is one, else the calculated band. */
    effectiveBand: z.enum(['low', 'moderate', 'high']).nullable(),
    eddRequired: z.boolean().nullable(),
    eddReasons: z.array(z.string()).nullable(),
    eddStatus: amlEddStatusSchema.nullable(),
    eddNotes: z.string().nullable(),
    eddCompletedAt: z.string().nullable(),
    eddCompletedByUserId: z.string().nullable(),
    eddCompletedByName: z.string().nullable(),
    decision: amlDecisionSchema.nullable(),
    complianceReviewerUserId: z.string().nullable(),
    complianceReviewerName: z.string().nullable(),
    complianceDecisionAt: z.string().nullable(),
    complianceNotes: z.string().nullable(),
    /** Set only by an override. Null means the calculated band stands (§30). */
    finalBand: z.enum(['low', 'moderate', 'high']).nullable(),
    overrideReason: z.string().nullable(),
    overriddenByUserId: z.string().nullable(),
    overriddenByName: z.string().nullable(),
    overriddenAt: z.string().nullable(),
    /** The day the next review falls due (§32). */
    nextReviewDate: z.string().nullable(),
});
export type AmlAssessmentDto = z.infer<typeof amlAssessmentDtoSchema>;

/**
 * What the Contact's AML card shows: the risk band of the most recently
 * COMPLETED assessment. Every field is null when there is none.
 */
export const contactAmlSummarySchema = z.object({
    assessmentId: z.string().nullable(),
    reference: z.string().nullable(),
    band: riskBandSchema.nullable(),
    /** "Low" / "Medium" / "High". */
    bandLabel: z.string().nullable(),
    score: z.number().nullable(),
    completionDate: z.string().nullable(),
});
export type ContactAmlSummary = z.infer<typeof contactAmlSummarySchema>;

/**
 * The AML card summary + every assessment held against the Contact, newest
 * first.
 *
 * The identity position is NOT here. It reads the `AmlIdentityVerification` rows
 * of the subject, which `kycContract.listIdentityVerifications` already serves,
 * and `amlIdentityPosition` turns into the answer the card prints. One source,
 * one rule.
 */
export const listContactAmlAssessmentsResponseSchema = z.object({
    contactId: z.string(),
    contactName: z.string(),
    summary: contactAmlSummarySchema,
    assessments: z.array(amlAssessmentDtoSchema),
});
export type ListContactAmlAssessmentsResponse = z.infer<
    typeof listContactAmlAssessmentsResponseSchema
>;

/** Save body — the questionnaire's editable fields. Nothing derived is sent. */
const saveBodySchema = z.object({
    /** Absent → create a new draft; present → patch that draft. */
    id: z.string().optional(),
    answers: amlAnswersSchema,
    highRiskOccupation: z.string().nullable().optional(),
});

/**
 * Amend body (SEP-179) — the corrected content of a COMPLETED assessment.
 *
 * There is no `id` in the body: the record being corrected is the path
 * parameter, and the correction's own AML-#### is allocated server-side. The
 * assessment occasion (completion date, completed-by) is inherited from the
 * original and is deliberately not editable here — this endpoint fixes what was
 * recorded, not when it happened or who did it.
 */
const amendBodySchema = z.object({
    answers: amlAnswersSchema,
    highRiskOccupation: z.string().nullable().optional(),
    /** Why the record is being corrected. Optional free text. */
    amendmentReason: z.string().nullable().optional(),
});

/**
 * Amendment body of a SEP-228 record (§31, §38).
 *
 * The same supersede-and-replace mechanism as the SEP-171 correction above, and
 * two differences that the ticket makes.
 *
 * The reason is MANDATORY (§38): the audit history must say why a completed
 * assessment changed. And nothing calculated travels on the request — the
 * server re-scores the corrected answers through the shared instrument, against
 * the country register the amendment is made under (§35).
 */
const amendQuestionnaireBodySchema = z.object({
    answers: amlAnswersSchema,
    /** Mandatory. An amendment nobody can explain is not an audit trail (§38). */
    amendmentReason: z.string().min(1),
});

// --- The SEP-228 workflow bodies (§21, §26, §28, §29, §30) -----------------

/**
 * Start body — which question set the record answers (§8). The controller
 * derives the kind from the Contact and refuses a mismatch, so this is the
 * caller's statement of what it thinks it is opening.
 */
const startBodySchema = z.object({
    kind: z.enum(['individual', 'company']),
});

/** Submit body — nothing to decide. Ops presses one button (§21). */
const submitBodySchema = z.object({}).optional();

/** The EDD section Compliance completes (§26). */
const eddBodySchema = z.object({
    status: amlEddStatusSchema,
    /** Required to complete an EDD; a completion with no note tells nobody why. */
    notes: z.string().nullable().optional(),
});

/** Approve or reject a Moderate or High record (§28, §29). */
const decisionBodySchema = z.object({
    decision: z.enum(['approved', 'rejected']),
    /** The reviewer's own note. Required on a rejection. */
    notes: z.string().nullable().optional(),
});

/** Override the calculated risk (§30). The reason is never optional. */
const overrideBodySchema = z.object({
    finalBand: z.enum(['low', 'moderate', 'high']),
    reason: z.string().min(1),
});

/** Complete body — the "Completed By" decision (defaults to the caller). */
const completeBodySchema = z
    .object({
        completedByUserId: z.string().optional(),
    })
    .optional();

export const amlAssessmentContract = c.router(
    {
        listContactAmlAssessments: {
            method: 'GET',
            path: '/contacts/:contactId/aml-assessments',
            pathParams: z.object({ contactId: z.string() }),
            responses: {
                200: listContactAmlAssessmentsResponseSchema,
                403: errorBody,
                404: errorBody,
            },
            summary: "A Contact's AML card summary + assessment history (newest first)",
        },
        saveContactAmlAssessment: {
            method: 'POST',
            path: '/contacts/:contactId/aml-assessments/save',
            pathParams: z.object({ contactId: z.string() }),
            body: saveBodySchema,
            responses: {
                200: z.object({ assessment: amlAssessmentDtoSchema }),
                400: staffActionErrorSchema,
                403: staffActionErrorSchema,
                404: staffActionErrorSchema,
            },
            summary: "Create or patch a Contact's draft AML assessment",
        },
        completeAmlAssessment: {
            method: 'POST',
            path: '/contacts/aml-assessments/:id/complete',
            pathParams: z.object({ id: z.string() }),
            body: completeBodySchema,
            responses: {
                200: z.object({ assessment: amlAssessmentDtoSchema }),
                400: staffActionErrorSchema,
                403: staffActionErrorSchema,
                404: staffActionErrorSchema,
            },
            summary: 'Complete an AML assessment (draft → completed)',
        },
        amendAmlAssessment: {
            method: 'POST',
            path: '/contacts/aml-assessments/:id/amend',
            pathParams: z.object({ id: z.string() }),
            body: amendBodySchema,
            responses: {
                // The CORRECTION, not the record that was corrected.
                200: z.object({ assessment: amlAssessmentDtoSchema }),
                400: staffActionErrorSchema,
                403: staffActionErrorSchema,
                404: staffActionErrorSchema,
            },
            summary: 'Correct a completed AML assessment (supersedes the original)',
        },
        amendAmlQuestionnaire: {
            method: 'POST',
            path: '/contacts/aml-assessments/:id/amend-questionnaire',
            pathParams: z.object({ id: z.string() }),
            body: amendQuestionnaireBodySchema,
            responses: {
                // The AMENDMENT, not the record it supersedes.
                200: z.object({ assessment: amlAssessmentDtoSchema }),
                // A record that is not a completed SEP-228 one, an incomplete
                // answer set, or a missing reason (§31, §38), all read 400.
                400: staffActionErrorSchema,
                403: staffActionErrorSchema,
                404: staffActionErrorSchema,
            },
            summary: 'Amend a completed SEP-228 AML assessment (§31, Compliance and Admin)',
        },
        // --- The SEP-228 workflow doors --------------------------------------
        startAmlAssessment: {
            method: 'POST',
            path: '/contacts/:contactId/aml-assessments/start',
            pathParams: z.object({ contactId: z.string() }),
            body: startBodySchema,
            responses: {
                200: z.object({ assessment: amlAssessmentDtoSchema }),
                400: staffActionErrorSchema,
                403: staffActionErrorSchema,
                404: staffActionErrorSchema,
                // The identity verification gate holds the questionnaire (§5).
                409: staffActionErrorSchema,
            },
            summary: 'Open a SEP-228 AML questionnaire, once identity is verified',
        },
        submitAmlAssessment: {
            method: 'POST',
            path: '/contacts/aml-assessments/:id/submit',
            pathParams: z.object({ id: z.string() }),
            body: submitBodySchema,
            responses: {
                200: z.object({ assessment: amlAssessmentDtoSchema }),
                400: staffActionErrorSchema,
                403: staffActionErrorSchema,
                404: staffActionErrorSchema,
                409: staffActionErrorSchema,
            },
            summary: 'Submit the answers for scoring, and route the result (§21)',
        },
        setAmlEdd: {
            method: 'POST',
            path: '/contacts/aml-assessments/:id/edd',
            pathParams: z.object({ id: z.string() }),
            body: eddBodySchema,
            responses: {
                200: z.object({ assessment: amlAssessmentDtoSchema }),
                400: staffActionErrorSchema,
                403: staffActionErrorSchema,
                404: staffActionErrorSchema,
            },
            summary: 'Record the enhanced due diligence section (§26)',
        },
        decideAmlAssessment: {
            method: 'POST',
            path: '/contacts/aml-assessments/:id/decide',
            pathParams: z.object({ id: z.string() }),
            body: decisionBodySchema,
            responses: {
                200: z.object({ assessment: amlAssessmentDtoSchema }),
                400: staffActionErrorSchema,
                403: staffActionErrorSchema,
                404: staffActionErrorSchema,
                // An open EDD blocks an approval (§26).
                409: staffActionErrorSchema,
            },
            summary: 'Approve or reject a Moderate or High assessment (§28, §29)',
        },
        overrideAmlRisk: {
            method: 'POST',
            path: '/contacts/aml-assessments/:id/override',
            pathParams: z.object({ id: z.string() }),
            body: overrideBodySchema,
            responses: {
                200: z.object({ assessment: amlAssessmentDtoSchema }),
                400: staffActionErrorSchema,
                403: staffActionErrorSchema,
                404: staffActionErrorSchema,
            },
            summary: 'Override the calculated risk, with a mandatory reason (§30)',
        },
    },
    { pathPrefix: '/api', strictStatusCodes: false },
);
